import { LitElement, html, css, TemplateResult, PropertyValues } from 'lit';
import {property, customElement, state} from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import { VesselPerformanceModel } from '../../../src/models/vessel-performance-model';
import { RouteGeometry, VoyageRoutePointFeature } from '../../../src/models/voyage-routing-model';
import { findLastIndex } from '../../../src/utils/helpers';
import '../../voyage-spinner/src/voyage-spinner';
import { getRoutePropertyAverages } from '../../../src/utils/route-utils';
import { calculateTotalFuel } from '../../voyage-suggested-routes/src/helper';
import { getAvgSpeed, getTotalDistance } from '../../voyage-eta-change-review/src/helper';
import { ViewRouteStyles } from './voyage-view-route-styles';
import { SuggestedRouteEvents } from '../../voyage-suggested-routes/src/types';

@customElement('voyage-view-route')
export class VoyageViewRoute extends LitElement
{
    @property({type: String, reflect: true}) scheduleId: string = "";
    @state() private _approvedPlan: VoyagePlan | null = null;
    @state() private _isVoyageActive = false;
    @state() private _remainingDistance: string = '-';
    @state() private _remainingSpeed: string = '-';

    updated(changedProperties: PropertyValues) {
        changedProperties.forEach((oldValue, propName) => {
            if (propName === "scheduleId") {
                this._getPlans();
            }
        });
    }

    constructor()
    {
        super();
        this._onRouteCalculated = this._onRouteCalculated.bind(this);
        this._onExecutingPerformanceDataUpdated = this._onExecutingPerformanceDataUpdated.bind(this);
    }

    static styles = [ViewRouteStyles]

    render()
    {
        return html`
        <div class="container">
            ${this._calculating ? 
            html`<div class="calculating-container">
                    <div class="calculating-items">
                        <voyage-spinner></voyage-spinner>
                        <div>Calculating...</div>
                    </div>
                 </div>` :
            html``}
            <div class="summary">
                ${this._renderColumnValues(this._isVoyageActive ? this._approvedPlan : null, "REMAINING", "VOYAGE")}
                <div></div>
                <div class="center"><div class="label">CALCULATED<br>DISTANCE</div></div>
                <div class="center"><div class="label">CALCULATED<br>AVERAGE SPEED</div></div>
                <div class="center"><div class="label">CALCULATED<br>AVERAGE STW</div></div>
                <div class="center"><div class="label">CALCULATED<br>BUNKER CONSUMPTION</div></div>
                <div class="center"><div class="label">CALCULATED<br>AVERAGE RPM</div></div>
                <div class="center"><div class="label">PREDICTED<br>MAX. WIND SPEED</div></div>
                <div class="center"><div class="label">PREDICTED<br>MAX. FAV. CURRENTS</div></div>
                <div class="center"><div class="label">PREDICTED<br>MAX. WAVE SWELL</div></div>
                <div class="center"><div class="label">VOYAGE TIME WITH WEATHER WARNINGS</div></div>
                ${this._renderColumnValues(!this._isVoyageActive ? this._approvedPlan : null, "FULL", "VOYAGE")}
            </div>
            <div class="update-warning">Numbers here will not update while view is open</div>
            <button id="passagePlanBtn" @click=${() => this.dispatchEvent(new Event('show-passage-plan'))}>
                ${SvgIcons.ballotCheck}
                    Passage Plan
                <div class="planStatus">APPROVED</div>
            </button>
            <voyage-route-export show-above .plan=${this._approvedPlan}></voyage-route-export>
        </div>
        `;
    }

    connectedCallback()
    {
        super.connectedCallback();
        VesselPlanningModel!.addEventListener(SuggestedRouteEvents.PlanChanged,this._onRouteCalculated as EventListener);
        VesselPerformanceModel?.addEventListener('executing-performance-data-updated', this._onExecutingPerformanceDataUpdated as EventListener);
    }

    disconnectedCallback()
    {
        super.disconnectedCallback();
        VesselPlanningModel!.removeEventListener(SuggestedRouteEvents.PlanChanged,this._onRouteCalculated as EventListener);
        VesselPerformanceModel?.removeEventListener('executing-performance-data-updated', this._onExecutingPerformanceDataUpdated as EventListener);
    }

    @property({type: Boolean, attribute: false}) private _calculating: boolean = false;
    private _renderColumnValues(plan: VoyagePlan|null, header1: string, header2: string): TemplateResult
    {
        let distance = "-";
        let avgSpeed = "-";
        let stw = "-";
        let fuelConsumption = "-";
        let rpm = "-";
        let maxWind = "-";
        let maxFavourableCurrents = "-";
        let maxSwell = "-";
        let timeWithWarnings = "-";

        if (plan)
        {
            const route = plan.routes[plan.selectedRouteIndex];

            //here getTotalDistance gives us the total distance of the voyage and remaining distance is from the vessel's current voyage for the voyage 
            distance = this._isVoyageActive ? this._remainingDistance : getTotalDistance(route) ;
            avgSpeed = this._isVoyageActive ? this._remainingSpeed : getAvgSpeed(route);
            fuelConsumption = calculateTotalFuel(route.route_info?.totals);

            const wind10M = route.route_info!.weather.summary.Wind10M;
            if (wind10M)
            {
                maxWind = `${Math.round(wind10M.max.value)} m/s`;
            }

            // TODO: Add value for "max favourable currents" (not calculated currently)

            const swell = route.route_info!.weather.summary.Swell;
            if (swell)
            {
                maxSwell = `${swell.max.value.toFixed(1)} m`
            }

            timeWithWarnings = this._getTimeWithWeatherWarnings(route.route_geometry);

            const vesselPositionIndex = route.route_geometry.findIndex((geometry) => geometry.properties.vesselPositionAt)
            const startIdx = vesselPositionIndex > -1 ? vesselPositionIndex : 0; // only consider future part of voyage
            const endIdx = route.route_geometry.length-1;
            const averages = getRoutePropertyAverages(route, startIdx, endIdx)
            
            if (averages.rpm > 0) rpm = `${averages.rpm.toFixed(1).toString()} RPM`;
            if (averages.stw > 0) stw = `${(averages.stw*(3600/1852)).toFixed(1).toString()} kt`;
        }

        return html`
            <div class="center"><div class="value header">${header1}<br>${header2}</div></div>
            <div class="center"><div class="value">${distance}</div></div>
            <div class="center"><div class="value">${avgSpeed}</div></div>
            <div class="center"><div class="value">${stw}</div></div>
            <div class="center"><div class="value">${fuelConsumption}</div></div>
            <div class="center"><div class="value">${rpm}</div></div>
            <div class="center"><div class="value">${maxWind}</div></div>
            <div class="center"><div class="value">${maxFavourableCurrents}</div></div>
            <div class="center"><div class="value">${maxSwell}</div></div>
            <div class="center"><div class="value">${timeWithWarnings}</div></div>
        `;
    }

    private async _getPlans(): Promise<void>
    {

        if (!this.scheduleId) return;

        this._approvedPlan = null;
        this._isVoyageActive = false;

        const plans = await VesselPlanningModel!.getPlans(this.scheduleId);
        const approvedPlan = plans.find(p => p.status === 'approved');

        if (!approvedPlan) return;

        this._approvedPlan = approvedPlan;
        this._isVoyageActive = this._isActiveVoyage(approvedPlan);

        this._updateRemainingDataToBerth();
        this._calculating = false;
    }

    private _isActiveVoyage(plan: VoyagePlan): boolean {
        const now = Date.now();
        return now > plan.etd && now < plan.eta;
    }

    private _getTimeWithWeatherWarnings(route: RouteGeometry): string
    {
        let time = 0;
        const lastPassedIdx = findLastIndex(route, f => f.geometry.type === "Point" && f.properties.isPassed);
        for (let i = lastPassedIdx+1; i < route.length; i++)
        {
            if (route[i].geometry.type === "LineString" && route[i].properties.warnings &&
                (route[i].properties.warnings.weather.warn.length > 0 || route[i].properties.warnings.weather.avoid.length > 0))
            {
                // Get timestamp from start and end points
                const start = <VoyageRoutePointFeature>route.find( f => f.geometry.type === "Point" &&
                                               f.geometry.coordinates[0] === (<[number,number][]>route[i].geometry.coordinates)[0][0] &&
                                               f.geometry.coordinates[1] === (<[number,number][]>route[i].geometry.coordinates)[0][1]);
                const end = <VoyageRoutePointFeature>route.find( f => f.geometry.type === "Point" &&
                                               f.geometry.coordinates[0] === (<[number,number][]>route[i].geometry.coordinates)[1][0] &&
                                               f.geometry.coordinates[1] === (<[number,number][]>route[i].geometry.coordinates)[1][1]);
                
                const timeDiff = new Date(end.properties.timestamp!).getTime() - new Date(start.properties.timestamp!).getTime();
                time += timeDiff;
            }
        }

        // Pretty print the time
        const hours = time/(60*60*1000);
        if (hours === 0)
        {
            return "0";
        }
        if (hours < 0.1)
        {
            return `${hours.toFixed(2)} hrs`;
        }
        if (hours < 10)
        {
            return `${hours.toFixed(1)} hrs`;
        }
        
        return `${Math.round(hours)} hrs`;
    }

    private _onRouteCalculated = async () => {
        await this._refreshApprovedPlanAndRemainingDistance();
        this._calculating = false;
    };

    private _onExecutingPerformanceDataUpdated = async () => {
        await this._refreshApprovedPlanAndRemainingDistance();
    };

    private async _refreshApprovedPlanAndRemainingDistance(): Promise<void> {

        try {
            const plans = await VesselPlanningModel!.getPlans(this.scheduleId);
            const latestApprovedPlan = plans.find(p => p.status === 'approved') || null;

            if (latestApprovedPlan) {
                this._approvedPlan = latestApprovedPlan;
                this._isVoyageActive = this._isActiveVoyage(latestApprovedPlan);
            }

            this._updateRemainingDataToBerth();
        } catch (e) {
            console.error("Failed to refresh plan / update remaining data to berth", e);
            this._remainingDistance = '-';
            this._remainingSpeed = '-';
        }
    }

    private _updateRemainingDataToBerth(): void {
        this._remainingDistance = '-';
        this._remainingSpeed = '-';

        if (!this._approvedPlan || !this._isVoyageActive) return;

        const remainingDataToBerth = VesselPerformanceModel?.getRemainingDataToBerth();

        if (remainingDataToBerth?.distanceToBerth) {
            this._remainingDistance = `${remainingDataToBerth.distanceToBerth.toFixed(1)} nm`;
        }

        if (remainingDataToBerth?.avgSpeedToBerthEta) {
            this._remainingSpeed = `${remainingDataToBerth.avgSpeedToBerthEta.toFixed(1)} kt`;
        }
    }
};
